/**
 * Rejection Counter Widget
 * 
 * Displays count of rejected timesheets requiring employee's attention
 * Clickable to navigate to My Timesheets page
 */

import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle } from 'lucide-react';
import { useTimesheetForScreen } from '@/contexts/TimesheetContext';
import { cn } from '@/lib/utils';

interface RejectionCounterWidgetProps {
  onClick?: () => void;
  className?: string;
  screenId?: string;
}

export function RejectionCounterWidget({ onClick, className, screenId = 'dashboard' }: RejectionCounterWidgetProps) {
  const { currentEmployee, timesheets } = useTimesheetForScreen(screenId);

  // Count rejected timesheets for current employee
  const rejectedCount = useMemo(() => {
    return timesheets.filter(
      ts => ts.employeeId === currentEmployee.id && ts.status === 'rejected'
    ).length;
  }, [timesheets, currentEmployee.id]);

  // Don't render if no rejected timesheets
  if (rejectedCount === 0) {
    return null;
  }

  return (
    <Card 
      className={cn(
        'hover:shadow-lg transition-shadow cursor-pointer border-orange-200 dark:border-orange-900 bg-orange-50/50 dark:bg-orange-950/20',
        className
      )}
      onClick={onClick}
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">Timesheets Requiring Correction</CardTitle>
        <AlertCircle className="h-4 w-4 text-orange-600 dark:text-orange-400" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
          {rejectedCount}
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          {rejectedCount === 1 
            ? 'Click to view and correct' 
            : 'Click to view and correct'}
        </p>
      </CardContent>
    </Card>
  );
}
